/* Telling the server that something broke in somebody's browser.
 *
 * Until now a crash on a user's phone reached exactly one place: the console of
 * a browser nobody was looking at. The server has recorded its own errors for
 * a long time (lib/errors.js, the admin console's error page); this is the same
 * list, fed from the other side.
 *
 * Deliberately small and deliberately harmless:
 *
 *   · **Never throws, never awaits anything visible.** Reporting a failure must
 *     not become a second failure. `sendBeacon` where it exists — it survives
 *     the page being closed, which is when a lot of these happen — and a fire-
 *     and-forget `fetch` otherwise.
 *   · **Capped per page load.** A render loop that throws sixty times a second
 *     would otherwise be sixty requests a second from one phone.
 *   · **Only what describes the bug**: the message, the top of the stack, which
 *     screen, and the path. No form contents, no plan data.
 */

import { readLocal } from '../api';

const MAX_PER_LOAD = 5;
let sent = 0;
const seen = new Set<string>();

export function reportClientError(
  error: unknown,
  { where, componentStack }: { where: string; componentStack?: string }
): void {
  try {
    if (sent >= MAX_PER_LOAD) return;
    const e = error instanceof Error ? error : new Error(String(error));
    const key = `${where}|${e.message}`;
    if (seen.has(key)) return;
    seen.add(key);
    sent++;

    const body = JSON.stringify({
      message: e.message.slice(0, 500),
      stack: (e.stack || '').split('\n').slice(0, 12).join('\n').slice(0, 3000),
      componentStack: (componentStack || '').split('\n').slice(0, 8).join('\n').slice(0, 1500),
      where: where.slice(0, 80),
      path: location.pathname.slice(0, 200)
    });

    const token = readLocal('amon_token');
    /* The beacon cannot carry an Authorization header, so it is only used when
       there is no session to attribute the error to. */
    if (!token && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon('/api/client-errors', new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch('/api/client-errors', {
      method: 'POST',
      keepalive: true,
      headers: {
        'content-type': 'application/json',
        'x-amon-client': '1',
        ...(token ? { authorization: `Bearer ${token}` } : {})
      },
      body
    }).catch(() => { /* nothing more to do */ });
  } catch {
    /* A reporter that fails is a reporter that stays quiet. */
  }
}
